/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import ProfileFavorites from "./ProfileFavorites";
import LifeformPicker from "./LifeformPicker";

export default function CollectionViewer({
  isOwnProfile,
  canEditObservationMetadata = false,
  collectionViewOpen,
  setCollectionViewOpen,
  collectionPhotos,
  collectionIndex,
  setCollectionIndex,
  collectionName,
  setCollectionImageLoaded,
  collectionFullscreen,
  setCollectionFullscreen,
  editingForm,
  setEditingForm,
  setEditingSpecies,
  speciesWithPhotos,
  savePhotoMetadata,
  displayLocation,
  hasMappedLocation,
  formatDateForDisplay,
  resolveTaxonomy,
  collections,
  setCollectionPhotos,
  saveCollectionOrder,
  deleteCollection,
  collectionMeta,
  setCollectionMeta,
  canManageCollections = false,
  adminManageCollection,
}) {
  const [collectionPhotoFullscreen, setCollectionPhotoFullscreen] =
    useState(false);
  const [orderEditMode, setOrderEditMode] = useState(false);
  const [draggedPhotoIndex, setDraggedPhotoIndex] = useState(null);
  const [dragOverPhotoIndex, setDragOverPhotoIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [deletingCollection, setDeletingCollection] = useState(false);
  const [albumPage, setAlbumPage] = useState(0);
  const [mobileAlbumPage, setMobileAlbumPage] = useState(0);
  const [mobilePageOffset, setMobilePageOffset] = useState(0);
  const [mobilePageAnimating, setMobilePageAnimating] = useState(false);
  const [isDesktopAlbum, setIsDesktopAlbum] = useState(() =>
    typeof window === "undefined"
      ? true
      : window.matchMedia("(min-width: 1024px)").matches,
  );
  const [editingAlbumDetails, setEditingAlbumDetails] = useState(false);
  const [albumTitleDraft, setAlbumTitleDraft] = useState("");
  const [albumDescriptionDraft, setAlbumDescriptionDraft] = useState("");
  const [selectedSwapPhotoIndex, setSelectedSwapPhotoIndex] = useState(null);
  const [pendingSwapPhotoIndex, setPendingSwapPhotoIndex] = useState(null);
  const [photoAspectByUrl, setPhotoAspectByUrl] = useState({});
  const [pageTurn, setPageTurn] = useState(null);
  const [addPhotosOpen, setAddPhotosOpen] = useState(false);
  const [selectedPhotosToAdd, setSelectedPhotosToAdd] = useState([]);
  const [addingPhotos, setAddingPhotos] = useState(false);
  const [selectedSpeciesForPhotoPicker, setSelectedSpeciesForPhotoPicker] =
    useState(null);

  const [savingAlbumDetails, setSavingAlbumDetails] = useState(false);
  const [adminCollectionAction, setAdminCollectionAction] = useState(null);
  const [adminCollectionReason, setAdminCollectionReason] = useState("");
  const [adminCollectionError, setAdminCollectionError] = useState("");
  const [adminCollectionSaving, setAdminCollectionSaving] = useState(false);
  const [savingPhotoMetadata, setSavingPhotoMetadata] = useState(false);
  const pageTurnTimerRef = useRef(null);
  const pageTurnCleanupRef = useRef(null);
  const touchStartXRef = useRef(null);
  const mobilePageTimerRef = useRef(null);
  const mobilePageSettleTimerRef = useRef(null);
  const mobilePageFrameRef = useRef(null);

  const albumTitle = collectionMeta?.[collectionName]?.title || collectionName;
  const albumDescription =
    collectionMeta?.[collectionName]?.description || "No description";

  const currentCollectionPhoto = useMemo(
    () => collectionPhotos[collectionIndex] || null,
    [collectionIndex, collectionPhotos],
  );

  const currentCollectionSpecies = useMemo(
    () =>
      Array.isArray(speciesWithPhotos)
        ? speciesWithPhotos.find((species) =>
            (species.forms || []).some(
              (form) => form?.url === currentCollectionPhoto?.url,
            ),
          ) || null
        : null,
    [currentCollectionPhoto?.url, speciesWithPhotos],
  );

  const currentCollectionForm = useMemo(
    () =>
      (currentCollectionSpecies?.forms || []).find(
        (form) => form?.url === currentCollectionPhoto?.url,
      ) || null,
    [currentCollectionPhoto?.url, currentCollectionSpecies],
  );

  const currentCollectionNotes = String(
    currentCollectionForm?.notes || currentCollectionPhoto?.notes || "",
  ).trim();

  async function saveAlbumDetails() {
    if (savingAlbumDetails) return;

    const updated = {
      title: albumTitleDraft.trim() || collectionName,
      description: albumDescriptionDraft.trim(),
    };

    setSavingAlbumDetails(true);

    try {
      if (canManageCollections) {
        if (typeof adminManageCollection !== "function") {
          throw new Error(
            "Administrator collection management is unavailable.",
          );
        }

        await adminManageCollection({
          action: "update_details",
          collectionName,
          title: updated.title,
          description: updated.description,
          reason: "Administrative collection metadata correction",
        });
      } else {
        if (!isOwnProfile || !auth.currentUser) return;

        await setDoc(
          doc(
            db,
            "users",
            auth.currentUser.uid,
            "collectionsMeta",
            collectionName,
          ),
          updated,
          {
            merge: true,
          },
        );
      }

      setCollectionMeta((prev) => ({
        ...prev,
        [collectionName]: {
          ...(prev[collectionName] || {}),
          ...updated,
        },
      }));

      setEditingAlbumDetails(false);
    } catch (error) {
      console.error("Failed to save collection details:", error);

      window.alert(
        error?.message || "The collection details could not be saved.",
      );
    } finally {
      setSavingAlbumDetails(false);
    }
  }

  async function requestCollectionDeletion() {
    if (deletingCollection || adminCollectionSaving) return;

    if (canManageCollections) {
      setAdminCollectionAction({
        type: "delete_collection",
        collectionName,
        albumTitle,
      });

      setAdminCollectionReason("");
      setAdminCollectionError("");
      return;
    }

    if (!isOwnProfile || typeof deleteCollection !== "function") {
      return;
    }

    setDeletingCollection(true);

    try {
      await deleteCollection(collectionName);
    } catch (error) {
      console.error("Failed to delete collection:", error);

      window.alert("The collection could not be deleted.");
    } finally {
      setDeletingCollection(false);
    }
  }

  function openAdminRemoveCurrentObservation() {
    if (!canManageCollections || adminCollectionSaving) {
      return;
    }

    const currentPhoto = collectionPhotos[collectionIndex];

    if (!currentPhoto?.url) {
      window.alert("The selected observation could not be found.");
      return;
    }

    const speciesDocument = speciesWithPhotos.find((species) =>
      (species.forms || []).some((form) => form.url === currentPhoto.url),
    );

    if (!speciesDocument) {
      window.alert("The selected species record could not be found.");
      return;
    }

    const form = (speciesDocument.forms || []).find(
      (speciesForm) => speciesForm.url === currentPhoto.url,
    );

    if (!form) {
      window.alert("The selected observation no longer exists.");
      return;
    }

    setAdminCollectionAction({
      type: "remove_observation",
      collectionName,
      speciesId: speciesDocument.id,
      scientificName:
        speciesDocument.name || currentPhoto.species || speciesDocument.id,
      commonName: speciesDocument.common || currentPhoto.common || "",
      formUrl: form.url,
      formLabel: form.label || "Default",
    });

    setAdminCollectionReason("");
    setAdminCollectionError("");
  }

  function closeAdminCollectionAction() {
    if (adminCollectionSaving) return;

    setAdminCollectionAction(null);
    setAdminCollectionReason("");
    setAdminCollectionError("");
  }

  async function confirmAdminCollectionAction() {
    if (
      !canManageCollections ||
      !adminCollectionAction ||
      adminCollectionSaving ||
      typeof adminManageCollection !== "function"
    ) {
      return;
    }

    const reason = adminCollectionReason.trim();

    if (reason.length < 10) {
      setAdminCollectionError(
        "Please provide a moderation reason of at least 10 characters.",
      );
      return;
    }

    if (reason.length > 500) {
      setAdminCollectionError(
        "The moderation reason cannot exceed 500 characters.",
      );
      return;
    }

    setAdminCollectionSaving(true);
    setAdminCollectionError("");

    try {
      if (adminCollectionAction.type === "remove_observation") {
        await adminManageCollection({
          action: "remove_observation",
          collectionName,
          speciesId: adminCollectionAction.speciesId,
          formUrl: adminCollectionAction.formUrl,
          reason,
        });

        const removedFormUrl = adminCollectionAction.formUrl;

        setCollectionPhotos((previousPhotos) =>
          previousPhotos.filter((photo) => photo.url !== removedFormUrl),
        );

        setCollectionFullscreen(false);
        setCollectionPhotoFullscreen(false);
        setCollectionIndex(0);
        setAlbumPage(0);
      }

      if (adminCollectionAction.type === "delete_collection") {
        await adminManageCollection({
          action: "delete_collection",
          collectionName,
          reason,
        });

        setCollectionMeta((previousMeta) => {
          const nextMeta = {
            ...previousMeta,
          };

          delete nextMeta[collectionName];

          return nextMeta;
        });

        setCollectionPhotos([]);
        setCollectionIndex(0);
        setCollectionFullscreen(false);
        setCollectionPhotoFullscreen(false);
        setEditingAlbumDetails(false);
        setAlbumPage(0);
        setCollectionViewOpen(false);
      }

      setAdminCollectionAction(null);
      setAdminCollectionReason("");
      setAdminCollectionError("");
    } catch (error) {
      console.error("Administrator collection action failed:", error);

      setAdminCollectionError(
        error?.message || "The collection action could not be completed.",
      );
    } finally {
      setAdminCollectionSaving(false);
    }
  }

  const albumSpreads = useMemo(() => {
    const spreads = [];
    let index = 0;

    function isNarrowPhoto(photo) {
      const measuredAspect = photoAspectByUrl[photo?.url];
      const storedWidth = Number(photo?.width || photo?.naturalWidth || 0);
      const storedHeight = Number(photo?.height || photo?.naturalHeight || 0);
      const storedAspect =
        storedWidth > 0 && storedHeight > 0 ? storedWidth / storedHeight : null;

      return (measuredAspect || storedAspect || 1) < 0.82;
    }

    function packPhotoRows(startIndex, maxRows = 2) {
      const rows = [];
      let nextIndex = startIndex;

      while (nextIndex < collectionPhotos.length && rows.length < maxRows) {
        const firstPhoto = collectionPhotos[nextIndex];
        const row = [{ photo: firstPhoto, photoIndex: nextIndex }];

        if (
          isNarrowPhoto(firstPhoto) &&
          nextIndex + 1 < collectionPhotos.length &&
          isNarrowPhoto(collectionPhotos[nextIndex + 1])
        ) {
          row.push({
            photo: collectionPhotos[nextIndex + 1],
            photoIndex: nextIndex + 1,
          });
          nextIndex += 2;
        } else {
          nextIndex += 1;
        }

        rows.push(row);
      }

      return { rows, nextIndex };
    }

    const firstRight = packPhotoRows(index, 2);
    spreads.push({
      leftIsTitle: true,
      leftRows: [],
      rightRows: firstRight.rows,
    });
    index = firstRight.nextIndex;

    while (index < collectionPhotos.length) {
      const left = packPhotoRows(index, 2);
      index = left.nextIndex;

      const right = packPhotoRows(index, 2);
      index = right.nextIndex;

      spreads.push({
        leftIsTitle: false,
        leftRows: left.rows,
        rightRows: right.rows,
      });
    }

    return spreads;
  }, [collectionPhotos, photoAspectByUrl]);

  const photoPageMap = useMemo(() => {
    const desktop = new Map();
    const mobile = new Map();

    albumSpreads.forEach((spread, spreadIndex) => {
      spread.leftRows.flat().forEach((item) => {
        desktop.set(item.photoIndex, spreadIndex);
        mobile.set(item.photoIndex, spreadIndex * 2);
      });

      spread.rightRows.flat().forEach((item) => {
        desktop.set(item.photoIndex, spreadIndex);
        mobile.set(item.photoIndex, spreadIndex * 2 + 1);
      });
    });

    return { desktop, mobile };
  }, [albumSpreads]);

  function getAlbumPageForPhotoIndex(targetIndex) {
    return photoPageMap.desktop.get(targetIndex) ?? 0;
  }

  function getMobileAlbumPageForPhotoIndex(targetIndex) {
    return photoPageMap.mobile.get(targetIndex) ?? 0;
  }

  function isPhotoVisibleOnCurrentPage(photoIndex) {
    if (isDesktopAlbum) {
      return getAlbumPageForPhotoIndex(photoIndex) === albumPage;
    }

    return getMobileAlbumPageForPhotoIndex(photoIndex) === mobileAlbumPage;
  }
  function turnAlbumPage(nextPage) {
    if (
      pageTurn ||
      nextPage === albumPage ||
      nextPage < 0 ||
      nextPage >= totalAlbumPages
    ) {
      return;
    }

    const direction = nextPage > albumPage ? "next" : "previous";

    setPageTurn({
      direction,
      from: albumPage,
      to: nextPage,
      active: false,
    });

    requestAnimationFrame(() => {
      setPageTurn((prev) => (prev ? { ...prev, active: true } : prev));
    });

    pageTurnTimerRef.current = window.setTimeout(() => {
      setAlbumPage(nextPage);

      pageTurnCleanupRef.current = window.setTimeout(() => {
        setPageTurn(null);
      }, 45);
    }, 340);
  }

  function turnAlbumToPage(targetPage) {
    if (
      pageTurn ||
      targetPage === albumPage ||
      targetPage < 0 ||
      targetPage >= totalAlbumPages
    ) {
      return;
    }

    turnAlbumPage(targetPage);
  }

  function recordPhotoAspect(photo, event) {
    if (!photo?.url) return;

    const img = event.currentTarget;
    if (!img.naturalWidth || !img.naturalHeight) return;

    const aspect = img.naturalWidth / img.naturalHeight;

    setPhotoAspectByUrl((prev) =>
      prev[photo.url] === aspect ? prev : { ...prev, [photo.url]: aspect },
    );
  }

  const committedAlbumSpread =
    albumSpreads[Math.min(albumPage, albumSpreads.length - 1)] ||
    albumSpreads[0];

  const targetAlbumSpread =
    pageTurn?.to !== undefined
      ? albumSpreads[Math.min(pageTurn.to, albumSpreads.length - 1)]
      : null;

  const backgroundAlbumSpread =
    pageTurn && targetAlbumSpread
      ? pageTurn.direction === "next"
        ? {
            ...committedAlbumSpread,
            rightRows: targetAlbumSpread.rightRows,
          }
        : {
            ...committedAlbumSpread,
            leftIsTitle: targetAlbumSpread.leftIsTitle,
            leftRows: targetAlbumSpread.leftRows,
          }
      : committedAlbumSpread;

  const mobileAlbumPageIsTitle = mobileAlbumPage === 0;

  function getMobileAlbumPageData(pageIndex) {
    if (pageIndex === 0) {
      return {
        isTitle: true,
        rows: [],
      };
    }

    const spreadIndex = Math.floor(pageIndex / 2);
    const spread = albumSpreads[spreadIndex];

    if (!spread) {
      return {
        isTitle: false,
        rows: [],
      };
    }

    return {
      isTitle: false,
      rows: pageIndex % 2 === 0 ? spread.leftRows : spread.rightRows,
    };
  }

  const mobileAlbumPageData = getMobileAlbumPageData(mobileAlbumPage);
  const mobileAlbumRows = mobileAlbumPageData.rows;
  const totalMobileAlbumPages =
    collectionPhotos.length === 0 ? 1 : Math.max(1, albumSpreads.length * 2);

  const totalAlbumPages = Math.max(1, albumSpreads.length);
  const selectedPhotoDesktopPage = getAlbumPageForPhotoIndex(collectionIndex);
  const selectedPhotoMobilePage =
    getMobileAlbumPageForPhotoIndex(collectionIndex);
  const activeRows = isDesktopAlbum
    ? [
        ...(albumSpreads[albumPage]?.leftRows || []),
        ...(albumSpreads[albumPage]?.rightRows || []),
      ]
    : mobileAlbumRows;
  const activePhotoNumbers = activeRows
    .flat()
    .map((item) => item.photoIndex + 1);
  const activePhotoRange =
    activePhotoNumbers.length === 0
      ? "Album cover"
      : activePhotoNumbers.length === 1
        ? `Photo ${activePhotoNumbers[0]}`
        : `Photos ${Math.min(...activePhotoNumbers)}–${Math.max(
            ...activePhotoNumbers,
          )}`;
  const activePageStatus = isDesktopAlbum
    ? `Spread ${albumPage + 1} of ${totalAlbumPages}`
    : `Page ${mobileAlbumPage + 1} of ${totalMobileAlbumPages}`;

  function animateMobilePageTo(nextPage) {
    if (
      mobilePageAnimating ||
      nextPage === mobileAlbumPage ||
      nextPage < 0 ||
      nextPage >= totalMobileAlbumPages
    ) {
      return;
    }

    const direction = nextPage > mobileAlbumPage ? 1 : -1;
    const pageWidth = Math.max(window.innerWidth, 320);

    if (mobilePageTimerRef.current) {
      window.clearTimeout(mobilePageTimerRef.current);
    }

    if (mobilePageSettleTimerRef.current) {
      window.clearTimeout(mobilePageSettleTimerRef.current);
    }

    setMobilePageAnimating(true);
    setMobilePageOffset(direction > 0 ? -pageWidth : pageWidth);

    mobilePageTimerRef.current = window.setTimeout(() => {
      setMobileAlbumPage(nextPage);
      setMobilePageAnimating(false);
      setMobilePageOffset(direction > 0 ? pageWidth : -pageWidth);

      mobilePageFrameRef.current = window.requestAnimationFrame(() => {
        mobilePageFrameRef.current = window.requestAnimationFrame(() => {
          setMobilePageAnimating(true);
          setMobilePageOffset(0);

          mobilePageSettleTimerRef.current = window.setTimeout(() => {
            setMobilePageAnimating(false);
          }, 220);
        });
      });
    }, 220);
  }

  function settleMobilePage() {
    if (mobilePageSettleTimerRef.current) {
      window.clearTimeout(mobilePageSettleTimerRef.current);
    }

    setMobilePageAnimating(true);
    setMobilePageOffset(0);

    mobilePageSettleTimerRef.current = window.setTimeout(() => {
      setMobilePageAnimating(false);
    }, 220);
  }

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const updateViewportMode = (event) => setIsDesktopAlbum(event.matches);

    setIsDesktopAlbum(mediaQuery.matches);
    mediaQuery.addEventListener?.("change", updateViewportMode);

    return () => mediaQuery.removeEventListener?.("change", updateViewportMode);
  }, []);

  useEffect(() => {
    if (!collectionViewOpen) return;

    setPageTurn(null);
    setMobilePageOffset(0);
    setMobilePageAnimating(false);

    if (isDesktopAlbum) {
      setAlbumPage(selectedPhotoDesktopPage);
    } else {
      setMobileAlbumPage(selectedPhotoMobilePage);
    }
  }, [
    collectionViewOpen,
    isDesktopAlbum,
    selectedPhotoDesktopPage,
    selectedPhotoMobilePage,
  ]);

  useEffect(
    () => () => {
      if (pageTurnTimerRef.current) {
        window.clearTimeout(pageTurnTimerRef.current);
      }

      if (pageTurnCleanupRef.current) {
        window.clearTimeout(pageTurnCleanupRef.current);
      }

      if (mobilePageTimerRef.current) {
        window.clearTimeout(mobilePageTimerRef.current);
      }

      if (mobilePageSettleTimerRef.current) {
        window.clearTimeout(mobilePageSettleTimerRef.current);
      }

      if (mobilePageFrameRef.current) {
        window.cancelAnimationFrame(mobilePageFrameRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!collectionViewOpen) return undefined;

    function handleAlbumKeyDown(event) {
      const isFormControl = ["INPUT", "TEXTAREA", "SELECT"].includes(
        event.target?.tagName,
      );

      if (event.key === "Escape") {
        if (collectionPhotoFullscreen) {
          setCollectionPhotoFullscreen(false);
        } else if (collectionFullscreen) {
          setCollectionFullscreen(false);
        } else if (editingForm) {
          setEditingForm(null);
          setEditingSpecies(null);
        } else {
          setCollectionViewOpen(false);
          setCollectionIndex(0);
        }

        return;
      }

      if (isFormControl || !["ArrowLeft", "ArrowRight"].includes(event.key)) {
        return;
      }

      event.preventDefault();
      const direction = event.key === "ArrowRight" ? 1 : -1;

      if (collectionPhotoFullscreen || collectionFullscreen) {
        setCollectionIndex((index) =>
          Math.max(0, Math.min(collectionPhotos.length - 1, index + direction)),
        );
      } else if (isDesktopAlbum) {
        if (!pageTurn) {
          setAlbumPage((page) =>
            Math.max(0, Math.min(totalAlbumPages - 1, page + direction)),
          );
        }
      } else {
        setMobileAlbumPage((page) =>
          Math.max(0, Math.min(totalMobileAlbumPages - 1, page + direction)),
        );
      }
    }

    window.addEventListener("keydown", handleAlbumKeyDown);
    return () => window.removeEventListener("keydown", handleAlbumKeyDown);
  }, [
    collectionFullscreen,
    collectionPhotoFullscreen,
    collectionPhotos.length,
    collectionViewOpen,
    editingForm,
    isDesktopAlbum,
    pageTurn,
    setCollectionFullscreen,
    setCollectionIndex,
    setCollectionViewOpen,
    setEditingForm,
    setEditingSpecies,
    totalAlbumPages,
    totalMobileAlbumPages,
  ]);

  useEffect(() => {
    if (!collectionViewOpen) return;
    setAlbumPage(0);
    setMobileAlbumPage(0);
    setMobilePageOffset(0);
    setMobilePageAnimating(false);
    setEditingAlbumDetails(false);
    setAddPhotosOpen(false);
    setSelectedPhotosToAdd([]);
    setSelectedSpeciesForPhotoPicker(null);
    setAlbumTitleDraft(albumTitle);
    setAlbumDescriptionDraft(
      albumDescription === "No description" ? "" : albumDescription,
    );
  }, [collectionViewOpen, collectionName, albumTitle, albumDescription]);

  function togglePhotoForCollection(photoItem) {
    const photoUrl = photoItem?.form?.url;
    if (!photoUrl) return;

    setSelectedPhotosToAdd((prev) => {
      const alreadySelected = prev.some((item) => item?.form?.url === photoUrl);

      if (alreadySelected) {
        return prev.filter((item) => item?.form?.url !== photoUrl);
      }

      return [...prev, photoItem];
    });
  }

  async function addSelectedPhotosToCollection() {
    if (addingPhotos || selectedPhotosToAdd.length === 0 || !auth.currentUser) {
      return;
    }

    setAddingPhotos(true);

    try {
      const addedAtBase = Date.now();
      const photosAddedToAlbum = [];

      const selectedBySpecies = selectedPhotosToAdd.reduce((grouped, item) => {
        if (!item?.speciesId || !item?.form?.url) return grouped;

        if (!grouped[item.speciesId]) {
          grouped[item.speciesId] = [];
        }

        grouped[item.speciesId].push(item);
        return grouped;
      }, {});

      for (const [speciesId, selectedItems] of Object.entries(
        selectedBySpecies,
      )) {
        const speciesRef = doc(
          db,
          "users",
          auth.currentUser.uid,
          "species",
          speciesId,
        );

        const speciesSnap = await getDoc(speciesRef);
        if (!speciesSnap.exists()) continue;

        const speciesData = speciesSnap.data();
        const forms = Array.isArray(speciesData.forms) ? speciesData.forms : [];

        const selectedUrls = new Set(
          selectedItems.map((item) => item.form.url),
        );

        const updatedForms = forms.map((form, formIndex) => {
          if (!selectedUrls.has(form.url)) return form;

          const alreadyInCollection = (form.collections || []).includes(
            collectionName,
          );

          const updatedForm = {
            ...form,
            collections: alreadyInCollection
              ? form.collections || []
              : [...(form.collections || []), collectionName],
            collectionAddedAt: {
              ...(form.collectionAddedAt || {}),
              [collectionName]:
                form.collectionAddedAt?.[collectionName] ||
                addedAtBase + photosAddedToAlbum.length,
            },
          };

          if (!collectionPhotos.some((photo) => photo.url === form.url)) {
            photosAddedToAlbum.push({
              ...updatedForm,
              species:
                selectedItems.find((item) => item.form.url === form.url)
                  ?.speciesName ||
                speciesData.name ||
                "",
              common:
                selectedItems.find((item) => item.form.url === form.url)
                  ?.commonName ||
                speciesData.common ||
                "",
              collectionOrder:
                updatedForm.collectionAddedAt[collectionName] ||
                addedAtBase + formIndex,
            });
          }

          return updatedForm;
        });

        await updateDoc(speciesRef, {
          forms: updatedForms,
        });
      }

      if (photosAddedToAlbum.length > 0) {
        setCollectionPhotos((prev) => {
          const existingUrls = new Set(prev.map((photo) => photo.url));

          return [
            ...prev,
            ...photosAddedToAlbum.filter(
              (photo) => !existingUrls.has(photo.url),
            ),
          ];
        });
      }

      await setDoc(
        doc(db, "profile", auth.currentUser.uid),
        {
          sectionUpdatedAt: {
            collections: Date.now(),
            taxonomy: Date.now(),
          },
        },
        { merge: true },
      );

      setSelectedPhotosToAdd([]);
      setSelectedSpeciesForPhotoPicker(null);
      setAddPhotosOpen(false);
    } catch (error) {
      console.error("Failed to add photos to collection:", error);
      window.alert("The selected photos could not be added.");
    } finally {
      setAddingPhotos(false);
    }
  }

  function moveCollectionPhoto(fromIndex, toIndex) {
    const isOriginalSlot =
      fromIndex === null || toIndex === null || fromIndex === toIndex;

    if (isOriginalSlot) {
      cancelCollectionDrag();
      return;
    }

    setCollectionPhotos((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      const adjustedToIndex = toIndex > fromIndex ? toIndex - 1 : toIndex;
      next.splice(adjustedToIndex, 0, moved);
      return next;
    });

    cancelCollectionDrag();
  }

  function cancelCollectionDrag() {
    setDraggedPhotoIndex(null);
    setDragOverPhotoIndex(null);
    setSelectedSwapPhotoIndex(null);
    setPendingSwapPhotoIndex(null);
  }

  async function handleSaveCollectionOrder() {
    if (savingOrder || typeof saveCollectionOrder !== "function") return;

    setSavingOrder(true);

    try {
      await saveCollectionOrder(collectionPhotos);
      setOrderEditMode(false);
      cancelCollectionDrag();
    } catch (error) {
      console.error("Failed to save collection order:", error);
      window.alert(error?.message || "The photo order could not be saved.");
    } finally {
      setSavingOrder(false);
    }
  }

  function handleCollectionSwapPress(photoIndex) {
    if (!orderEditMode) {
      setCollectionIndex(photoIndex);
      setCollectionFullscreen(true);
      return;
    }

    if (selectedSwapPhotoIndex === null) {
      setSelectedSwapPhotoIndex(photoIndex);
      setPendingSwapPhotoIndex(null);
      return;
    }

    if (selectedSwapPhotoIndex === photoIndex) {
      setSelectedSwapPhotoIndex(null);
      setPendingSwapPhotoIndex(null);
      return;
    }

    setPendingSwapPhotoIndex(photoIndex);
  }

  function confirmCollectionSwap() {
    if (selectedSwapPhotoIndex === null || pendingSwapPhotoIndex === null) {
      return;
    }

    setCollectionPhotos((prev) => {
      const next = [...prev];
      const selectedPhoto = next[selectedSwapPhotoIndex];

      next[selectedSwapPhotoIndex] = next[pendingSwapPhotoIndex];
      next[pendingSwapPhotoIndex] = selectedPhoto;

      return next;
    });

    setSelectedSwapPhotoIndex(null);
    setPendingSwapPhotoIndex(null);
  }

  function getCollectionPreviewPhotos() {
    const items = collectionPhotos.map((photo, index) => ({
      photo,
      originalIndex: index,
      isPlaceholder: false,
    }));

    if (
      !orderEditMode ||
      draggedPhotoIndex === null ||
      dragOverPhotoIndex === null ||
      draggedPhotoIndex === dragOverPhotoIndex
    ) {
      return items;
    }

    const remaining = items.filter(
      (item) => item.originalIndex !== draggedPhotoIndex,
    );

    const targetPosition = remaining.findIndex(
      (item) => item.originalIndex === dragOverPhotoIndex,
    );

    const insertAt = targetPosition === -1 ? remaining.length : targetPosition;

    const next = [...remaining];

    next.splice(insertAt, 0, {
      photo: null,
      originalIndex: dragOverPhotoIndex,
      isPlaceholder: true,
    });

    return next;
  }

  function openCurrentPhotoEditor() {
    if (!currentCollectionSpecies || !currentCollectionForm) {
      window.alert("This observation could not be found in the photo archive.");
      return;
    }

    const variant = String(currentCollectionForm.variant || "");
    const notes = String(currentCollectionForm.notes || "");
    const formCollections = Array.isArray(currentCollectionForm.collections)
      ? currentCollectionForm.collections
      : [];

    setEditingSpecies(currentCollectionSpecies);
    setEditingForm({
      ...currentCollectionForm,
      date: {
        year: currentCollectionForm?.date?.year || "NA",
        month: currentCollectionForm?.date?.month || "NA",
        day: currentCollectionForm?.date?.day || "NA",
      },
      location: String(
        currentCollectionForm.locationName ||
          currentCollectionForm.location ||
          "",
      ),
      locationName: String(
        currentCollectionForm.locationName ||
          currentCollectionForm.location ||
          "",
      ),
      locationLat: currentCollectionForm.locationLat ?? null,
      locationLng: currentCollectionForm.locationLng ?? null,
      variant,
      notes,
      collections: formCollections,
      showAdditionalInfo: false,
      showVariantField:
        variant.trim() !== "" && variant.trim().toLowerCase() !== "na",
      showCollectionsField: formCollections.length > 0,
      showNotesField: notes.trim() !== "",
    });

    setCollectionFullscreen(false);
    setCollectionPhotoFullscreen(false);
  }

  async function handleSavePhotoMetadata() {
    if (savingPhotoMetadata || typeof savePhotoMetadata !== "function") return;

    setSavingPhotoMetadata(true);

    try {
      await savePhotoMetadata();
    } catch (error) {
      console.error("Failed to save photo metadata:", error);
      window.alert(error?.message || "The photo details could not be saved.");
    } finally {
      setSavingPhotoMetadata(false);
    }
  }

  function closePhotoEditor() {
    setEditingForm(null);
    setEditingSpecies(null);
  }

  function renderAlbumRows(rows) {
    return rows.map((row, rowIndex) => (
      <div
        key={`album-row-${rowIndex}-${row.map((item) => item.photoIndex).join("-")}`}
        className={`flex min-h-0 flex-1 items-center justify-center overflow-visible ${
          row.length > 1 ? "gap-3" : ""
        }`}
      >
        {row.map(({ photo, photoIndex }) => (
          <button
            key={photo.url || photoIndex}
            type="button"
            onClick={() => {
              setCollectionIndex(photoIndex);
              setCollectionFullscreen(true);
            }}
            className={`group relative bg-transparent p-0 transition hover:-translate-y-1 ${
              row.length > 1
                ? "max-w-[48%] lg:max-w-[46%]"
                : "max-w-[96%] lg:max-w-[86%]"
            }`}
          >
            <div className="relative z-0 flex items-center justify-center overflow-visible">
              <div className="bg-white px-2 pt-2 pb-2 border border-[#e7e3d4] shadow-none">
                <img
                  src={photo.url}
                  alt={photo.species || collectionName}
                  onLoad={(event) => recordPhotoAspect(photo, event)}
                  decoding="async"
                  className="block max-h-[clamp(9rem,28dvh,15rem)] max-w-full object-contain lg:max-h-[min(15rem,30vh)]"
                />

                <div
                  className="mt-1 w-full truncate text-center text-[10px] leading-none tracking-[0.02em] text-[#6f6f68]"
                  style={{
                    fontFamily:
                      '"Bradley Hand", "Segoe Print", "Comic Sans MS", cursive',
                  }}
                >
                  {photo.species || "—"}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    ));
  }

  if (!collectionViewOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-hidden bg-[#20292f] px-0 pb-0 pt-0 lg:items-center lg:bg-[#182126]/90 lg:p-3 lg:backdrop-blur-sm"
      onClick={() => {
        if (orderEditMode) {
          cancelCollectionDrag();
          return;
        }

        setCollectionViewOpen(false);
        setCollectionFullscreen(false);
        setCollectionPhotoFullscreen(false);
        setCollectionIndex(0);
      }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 hidden bg-[radial-gradient(circle_at_50%_15%,rgba(147,202,135,0.16),transparent_38%),linear-gradient(180deg,rgba(255,255,255,0.04),transparent_45%)] lg:block"
      />

      {addPhotosOpen && (
        <div onClick={(event) => event.stopPropagation()}>
          <ProfileFavorites
            isOwnProfile={isOwnProfile}
            profile={{ favorites: [] }}
            savedMessage={false}
            openFavoriteViewer={() => {}}
            openFavoriteSelector={() => {}}
            favSelectorOpen={addPhotosOpen}
            setFavSelectorOpen={(open) => {
              setAddPhotosOpen(open);

              if (!open) {
                setSelectedPhotosToAdd([]);
                setSelectedSpeciesForPhotoPicker(null);
              }
            }}
            selectedSpeciesForFav={selectedSpeciesForPhotoPicker}
            setSelectedSpeciesForFav={setSelectedSpeciesForPhotoPicker}
            speciesWithPhotos={speciesWithPhotos}
            assignFavorite={() => {}}
            collectionPickerMode
            collectionName={collectionName}
            collectionPhotos={collectionPhotos}
            selectedCollectionPhotos={selectedPhotosToAdd}
            onToggleCollectionPhoto={togglePhotoForCollection}
            onAddCollectionPhotos={addSelectedPhotosToCollection}
            addingCollectionPhotos={addingPhotos}
          />
        </div>
      )}

      {adminCollectionAction && canManageCollections && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-3 py-6"
          onClick={(event) => {
            event.stopPropagation();
            closeAdminCollectionAction();
          }}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-3xl border border-red-200 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-red-100 bg-red-50 px-6 py-5 text-center">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-red-600">
                Administrator Moderation
              </div>

              <h3
                className="mt-1 text-3xl text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                {adminCollectionAction.type === "delete_collection"
                  ? "Delete Collection"
                  : "Remove from Collection"}
              </h3>
            </div>

            <div className="p-6">
              <div className="rounded-2xl border border-[#93CA87]/35 bg-[#FCFFF6] px-4 py-4 text-center">
                {adminCollectionAction.type === "delete_collection" ? (
                  <>
                    <p className="text-sm font-semibold text-[#374049]">
                      {adminCollectionAction.albumTitle ||
                        adminCollectionAction.collectionName}
                    </p>

                    <p className="mt-2 text-xs leading-relaxed text-slate-500">
                      The collection record and all collection references will
                      be removed. Every photo and species observation will
                      remain in the user’s archive.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-[#374049]">
                      <span className="italic">
                        {adminCollectionAction.scientificName}
                      </span>

                      {adminCollectionAction.commonName && (
                        <span className="font-normal">
                          {" "}
                          ({adminCollectionAction.commonName})
                        </span>
                      )}
                    </p>

                    <p className="mt-2 text-xs leading-relaxed text-slate-500">
                      This photo will remain in the user’s archive and in any
                      other collections to which it belongs.
                    </p>
                  </>
                )}
              </div>

              <label className="mt-5 block">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#374049]">
                  Moderation reason
                </span>

                <textarea
                  value={adminCollectionReason}
                  onChange={(event) => {
                    setAdminCollectionReason(event.target.value);
                    setAdminCollectionError("");
                  }}
                  disabled={adminCollectionSaving}
                  maxLength={500}
                  rows={4}
                  placeholder="Explain why this collection action is necessary..."
                  className="mt-2 w-full resize-none rounded-2xl border border-[#93CA87]/45 bg-white px-4 py-3 text-sm text-[#374049] outline-none transition focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/15 disabled:bg-slate-50"
                />
              </label>

              <div className="mt-1 text-right text-[10px] text-slate-400">
                {adminCollectionReason.length}/500
              </div>

              {adminCollectionError && (
                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">
                  {adminCollectionError}
                </div>
              )}

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeAdminCollectionAction}
                  disabled={adminCollectionSaving}
                  className="rounded-xl border border-[#93CA87]/45 bg-white px-5 py-2.5 text-sm font-semibold text-[#374049] transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={confirmAdminCollectionAction}
                  disabled={
                    adminCollectionSaving ||
                    adminCollectionReason.trim().length < 10
                  }
                  className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                >
                  {adminCollectionSaving
                    ? "Applying Action..."
                    : adminCollectionAction.type === "delete_collection"
                      ? "Delete Collection"
                      : "Remove from Collection"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10">
        <div
          className="min-h-0 w-screen max-w-none overflow-visible lg:w-auto lg:max-w-[98vw]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="max-h-none overflow-visible p-0">
            {collectionFullscreen && (
              <div
                className="fixed inset-0 z-[70] bg-black/40 flex items-start sm:items-center justify-center px-0 sm:px-4 pt-0 sm:pt-20 pb-0 sm:pb-6 overflow-y-auto"
                onClick={() => {
                  setCollectionFullscreen(false);
                  setCollectionPhotoFullscreen(false);
                }}
              >
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <div className="min-h-screen w-screen max-w-none overflow-hidden rounded-none border border-white/70 bg-white shadow-2xl sm:min-h-0 sm:w-[min(96vw,78rem)] sm:rounded-[2rem] sm:bg-[#f4f6f8]">
                    <div className="max-h-none overflow-y-auto p-3 sm:grid sm:max-h-[90vh] sm:grid-cols-[minmax(0,1.45fr)_minmax(19rem,0.75fr)] sm:gap-5 sm:overflow-hidden sm:p-5">
                      <div className="mb-4 flex justify-center sm:mb-0">
                        <div className="relative w-full flex items-center justify-center bg-transparent min-h-[40vh]">
                          <div className="relative inline-block max-w-full">
                            <img
                              src={collectionPhotos[collectionIndex]?.url}
                              alt={
                                collectionPhotos[collectionIndex]?.species ||
                                collectionName
                              }
                              onClick={() => setCollectionPhotoFullscreen(true)}
                              decoding="async"
                              className="block max-h-[62vh] max-w-full cursor-zoom-in object-contain sm:max-h-[82vh]"
                            />

                            <div
                              className="absolute top-3 right-3 left-3 z-30 flex flex-wrap justify-end gap-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {(isOwnProfile || canEditObservationMetadata) && (
                                <button
                                  type="button"
                                  onClick={openCurrentPhotoEditor}
                                  className={`rounded-md px-3 py-1 text-xs font-medium shadow-sm backdrop-blur transition ${
                                    isOwnProfile
                                      ? "bg-white/85 text-[#374049] hover:bg-white"
                                      : "bg-[#374049] text-white hover:bg-[#01767A]"
                                  }`}
                                >
                                  {isOwnProfile ? "Edit" : "Admin Edit"}
                                </button>
                              )}

                              {canManageCollections && (
                                <button
                                  type="button"
                                  onClick={openAdminRemoveCurrentObservation}
                                  disabled={adminCollectionSaving}
                                  className="rounded-md border border-red-300 bg-red-50 px-3 py-1 text-xs font-medium text-red-600 shadow-sm transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Remove from Collection
                                </button>
                              )}

                              <button
                                onClick={() => {
                                  setCollectionFullscreen(false);
                                  setCollectionPhotoFullscreen(false);
                                }}
                                className="rounded-md bg-black/65 backdrop-blur px-3 py-1 text-xs font-medium text-white shadow-sm hover:bg-black/80 transition"
                              >
                                Exit
                              </button>
                            </div>

                            {collectionPhotos.length > 1 && (
                              <>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (collectionIndex === 0) return;
                                    setCollectionImageLoaded(false);
                                    setCollectionIndex((i) =>
                                      Math.max(0, i - 1),
                                    );
                                  }}
                                  className={`absolute left-2 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full backdrop-blur-md border border-white/20 flex items-center justify-center transition ${
                                    collectionIndex === 0
                                      ? "bg-white/5 opacity-20 cursor-default"
                                      : "bg-black/20 text-white hover:bg-black/35 active:scale-95"
                                  }`}
                                >
                                  ‹
                                </button>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (
                                      collectionIndex ===
                                      collectionPhotos.length - 1
                                    )
                                      return;
                                    setCollectionImageLoaded(false);
                                    setCollectionIndex((i) =>
                                      Math.min(
                                        collectionPhotos.length - 1,
                                        i + 1,
                                      ),
                                    );
                                  }}
                                  className={`absolute right-2 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full backdrop-blur-md border border-white/20 flex items-center justify-center transition ${
                                    collectionIndex ===
                                    collectionPhotos.length - 1
                                      ? "bg-white/5 opacity-20 cursor-default"
                                      : "bg-black/20 text-white hover:bg-black/35 active:scale-95"
                                  }`}
                                >
                                  ›
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="min-w-0 overflow-y-auto px-1 pb-5 sm:max-h-[82vh] sm:px-2 sm:pb-2">
                        <div className="mt-2 px-1 sm:px-2">
                          <h3
                            className="text-3xl italic text-slate-900 leading-tight text-center"
                            style={{ fontFamily: "Cormorant Garamond" }}
                          >
                            {collectionPhotos[collectionIndex]?.species ||
                              collectionName}
                            {collectionPhotos[collectionIndex]?.species && (
                              <span className="not-italic text-slate-700">
                                {" "}
                                (
                                {(() => {
                                  const family =
                                    resolveTaxonomy(
                                      collectionPhotos[
                                        collectionIndex
                                      ].species.split(" ")[0],
                                    )?.family || "family unknown";

                                  return (
                                    family.charAt(0).toUpperCase() +
                                    family.slice(1)
                                  );
                                })()}
                                )
                              </span>
                            )}
                          </h3>

                          <p className="text-slate-600 mt-0.5 text-center">
                            {collectionPhotos[collectionIndex]?.common || ""}
                          </p>
                        </div>

                        <div className="mt-4 px-1 sm:px-2">
                          <div className="rounded-2xl bg-[#f4f6f8] sm:bg-white/70 border border-white shadow-sm p-4">
                            {displayLocation(
                              collectionPhotos[collectionIndex],
                            ) &&
                              displayLocation(
                                collectionPhotos[collectionIndex],
                              ) !== "Unknown" && (
                                <div className="flex items-start gap-3">
                                  <div className="mt-0.5 text-slate-400">⌖</div>

                                  <div className="min-w-0">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                                      Location
                                    </div>

                                    <div className="text-sm text-[#374049] leading-snug">
                                      {displayLocation(
                                        collectionPhotos[collectionIndex],
                                      )}
                                      {hasMappedLocation(
                                        collectionPhotos[collectionIndex],
                                      ) && (
                                        <span className="ml-2 inline-flex items-center rounded-full bg-[#24C3BE]/10 border border-[#24C3BE]/30 px-2 py-[1px] text-[10px] font-medium text-[#24C3BE]">
                                          ✓
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                            <div className="mt-4 flex flex-wrap gap-2">
                              {formatDateForDisplay(
                                collectionPhotos[collectionIndex]?.date,
                              ) !== "Unknown" && (
                                <div className="rounded-full bg-[#374049] border border-[#93ca87]/40 px-3 py-1.5 text-xs text-white font-medium">
                                  <span className="text-[#24C3BE]">Date</span>
                                  <span className="mx-1.5 text-slate-300">
                                    •
                                  </span>
                                  {formatDateForDisplay(
                                    collectionPhotos[collectionIndex]?.date,
                                  )}
                                </div>
                              )}

                              {collectionPhotos[collectionIndex]?.label && (
                                <div className="rounded-full bg-[#374049] border border-[#93ca87]/40 px-3 py-1.5 text-xs text-white font-medium">
                                  <span className="text-[#24C3BE]">Lifeform</span>
                                  <span className="mx-1.5 text-slate-300">
                                    •
                                  </span>
                                  {collectionPhotos[collectionIndex].label}
                                </div>
                              )}

                              {collectionPhotos[collectionIndex]?.variant &&
                                collectionPhotos[collectionIndex].variant !==
                                  "NA" && (
                                  <div className="rounded-full bg-[#374049] border border-[#93ca87]/40 px-3 py-1.5 text-xs text-white font-medium">
                                    <span className="text-[#24C3BE]">
                                      Variant
                                    </span>
                                    <span className="mx-1.5 text-slate-300">
                                      •
                                    </span>
                                    {collectionPhotos[collectionIndex].variant}
                                  </div>
                                )}
                            </div>

                            {currentCollectionNotes && (
                              <div className="mt-4 border-t border-[#93CA87]/30 pt-4">
                                <div className="flex items-start gap-3">
                                  <div className="mt-0.5 shrink-0 text-[#24C3BE]">
                                    ✎
                                  </div>

                                  <div className="w-0 min-w-0 flex-1">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                                      Notes
                                    </div>

                                    <p className="mt-1 max-w-full whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700 [overflow-wrap:anywhere]">
                                      {currentCollectionNotes}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {collectionPhotoFullscreen && (
              <div
                className="fixed inset-0 z-[90] bg-black/95 flex items-center justify-center"
                onClick={() => setCollectionPhotoFullscreen(false)}
              >
                <img
                  src={collectionPhotos[collectionIndex]?.url}
                  alt={
                    collectionPhotos[collectionIndex]?.species || collectionName
                  }
                  className="max-w-[95vw] max-h-[95vh] object-contain cursor-zoom-out"
                  onClick={(e) => e.stopPropagation()}
                />

                {collectionPhotos.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (collectionIndex === 0) return;
                      setCollectionIndex((i) => i - 1);
                    }}
                    className={`absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full backdrop-blur-md border border-white/20 transition-all duration-200 ${
                      collectionIndex === 0
                        ? "bg-white/5 opacity-30 text-white cursor-default"
                        : "bg-white/10 text-white hover:bg-white/20 hover:scale-105 active:scale-95 cursor-pointer"
                    }`}
                  >
                    ‹
                  </button>
                )}

                {collectionPhotos.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (collectionIndex === collectionPhotos.length - 1)
                        return;
                      setCollectionIndex((i) => i + 1);
                    }}
                    className={`absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full backdrop-blur-md border border-white/20 transition-all duration-200 ${
                      collectionIndex === collectionPhotos.length - 1
                        ? "bg-white/5 opacity-30 text-white cursor-default"
                        : "bg-white/10 text-white hover:bg-white/20 hover:scale-105 active:scale-95 cursor-pointer"
                    }`}
                  >
                    ›
                  </button>
                )}
              </div>
            )}
            {!editingForm && (
              <div className="flex flex-col items-center justify-center">
                <div className="flex h-[5.5rem] w-screen max-w-none items-start justify-center pb-2 lg:mb-2 lg:h-auto lg:w-[min(96vw,90rem)] lg:max-w-[90rem] lg:items-center lg:pb-0">
                  <div className="w-full max-w-full border border-white/10 bg-[#273139] px-2 py-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.22)] lg:w-auto lg:min-w-[32rem] lg:px-3 lg:py-2">
                    <div className="mb-1 flex items-center justify-between gap-2 overflow-x-auto px-1 text-[10px] font-medium text-white/70 [scrollbar-width:none]">
                      <div className="flex shrink-0 items-center gap-2 uppercase tracking-[0.14em]">
                        <span>{activePageStatus}</span>
                        <span className="normal-case tracking-normal text-[#8fe0d3]">
                          {activePhotoRange}
                        </span>
                      </div>

                      <div className="flex shrink-0 items-center gap-1 normal-case tracking-normal">
                        {(isOwnProfile || canManageCollections) && (
                          <button
                            type="button"
                            onClick={() => {
                              setAlbumTitleDraft(albumTitle);
                              setAlbumDescriptionDraft(
                                albumDescription === "No description"
                                  ? ""
                                  : albumDescription,
                              );
                              setEditingAlbumDetails(true);
                              setPageTurn(null);
                              setAlbumPage(0);
                              setMobileAlbumPage(0);
                            }}
                            className="rounded bg-white/10 px-2 py-0.5 text-white transition hover:bg-white/20"
                          >
                            Details
                          </button>
                        )}

                        {isOwnProfile && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPhotosToAdd([]);
                              setSelectedSpeciesForPhotoPicker(null);
                              setAddPhotosOpen(true);
                            }}
                            className="rounded bg-white/10 px-2 py-0.5 text-white transition hover:bg-white/20"
                          >
                            + Photos
                          </button>
                        )}

                        {isOwnProfile && (
                          <button
                            type="button"
                            onClick={() => {
                              if (orderEditMode) {
                                handleSaveCollectionOrder();
                              } else {
                                setOrderEditMode(true);
                                cancelCollectionDrag();
                              }
                            }}
                            disabled={savingOrder}
                            className={`rounded px-2 py-0.5 transition disabled:opacity-50 ${
                              orderEditMode
                                ? "bg-[#24c3be] text-white"
                                : "bg-white/10 text-white hover:bg-white/20"
                            }`}
                          >
                            {savingOrder
                              ? "Saving..."
                              : orderEditMode
                                ? "Save order"
                                : "Reorder"}
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            setCollectionViewOpen(false);
                            setCollectionFullscreen(false);
                            setCollectionPhotoFullscreen(false);
                            setCollectionIndex(0);
                          }}
                          className="rounded bg-white/10 px-2 py-0.5 text-white transition hover:bg-white/20"
                        >
                          Close
                        </button>
                      </div>
                    </div>

                    <div className="flex max-w-full gap-1.5 overflow-x-auto bg-[#FAFAEB] bg-[repeating-linear-gradient(135deg,rgba(214,203,150,0.10)_0px,rgba(214,203,150,0.10)_1px,transparent_1px,transparent_8px)] px-2 py-1.5 shadow-inner [scrollbar-width:thin]">
                      {getCollectionPreviewPhotos().map(
                        (item, displayIndex) => {
                          if (item.isPlaceholder) {
                            return (
                              <div
                                key={`drop-placeholder-${displayIndex}`}
                                onDragOver={(e) => {
                                  if (!orderEditMode) return;
                                  e.preventDefault();
                                }}
                                onDrop={(e) => {
                                  if (!orderEditMode) return;
                                  e.preventDefault();
                                  e.stopPropagation();

                                  moveCollectionPhoto(
                                    draggedPhotoIndex,
                                    item.originalIndex,
                                  );
                                }}
                                className="h-9 w-9 shrink-0 border border-dashed border-[#24c3be]/70 bg-[#24c3be]/10 shadow-[inset_0_0_8px_rgba(36,195,190,0.18)] lg:h-10 lg:w-10"
                              />
                            );
                          }

                          const photo = item.photo;
                          const photoIndex = item.originalIndex;

                          return (
                            <button
                              key={photo.url || photoIndex}
                              type="button"
                              draggable={orderEditMode}
                              onDragStart={() => {
                                if (!orderEditMode) return;
                                setDraggedPhotoIndex(photoIndex);
                              }}
                              onDragOver={(e) => {
                                if (!orderEditMode) return;
                                e.preventDefault();

                                if (dragOverPhotoIndex !== photoIndex) {
                                  setDragOverPhotoIndex(photoIndex);
                                }
                              }}
                              onDrop={(e) => {
                                if (!orderEditMode) return;
                                e.preventDefault();
                                e.stopPropagation();

                                moveCollectionPhoto(
                                  draggedPhotoIndex,
                                  photoIndex,
                                );
                              }}
                              onDragEnd={cancelCollectionDrag}
                              onClick={() => {
                                if (orderEditMode) {
                                  handleCollectionSwapPress(photoIndex);
                                  return;
                                }

                                setCollectionIndex(photoIndex);

                                if (!isDesktopAlbum) {
                                  setPageTurn(null);
                                  setMobileAlbumPage(
                                    getMobileAlbumPageForPhotoIndex(photoIndex),
                                  );
                                  return;
                                }

                                turnAlbumToPage(
                                  getAlbumPageForPhotoIndex(photoIndex),
                                );
                              }}
                              aria-current={
                                isPhotoVisibleOnCurrentPage(photoIndex)
                                  ? "page"
                                  : undefined
                              }
                              className={`relative h-9 w-9 shrink-0 overflow-hidden border bg-[#FCFFF6] shadow-sm transition lg:h-10 lg:w-10 ${
                                orderEditMode &&
                                draggedPhotoIndex !== null &&
                                draggedPhotoIndex !== photoIndex
                                  ? "opacity-35 grayscale"
                                  : ""
                              } ${
                                orderEditMode &&
                                draggedPhotoIndex === photoIndex
                                  ? "z-20 scale-110 border-[#24c3be] opacity-100 shadow-[0_0_14px_rgba(36,195,190,0.35)]"
                                  : ""
                              } ${
                                orderEditMode
                                  ? "cursor-grab active:cursor-grabbing border-[#93CA87]/50"
                                  : isPhotoVisibleOnCurrentPage(photoIndex)
                                    ? "border-[#24c3be] ring-2 ring-[#24c3be]/25"
                                    : "border-[#93ca87]/30 opacity-70 hover:opacity-100 hover:border-[#24c3be]/50"
                              } ${
                                selectedSwapPhotoIndex === photoIndex
                                  ? "ring-2 ring-[#24c3be]/40 border-[#24c3be]"
                                  : ""
                              } ${
                                pendingSwapPhotoIndex === photoIndex
                                  ? "ring-2 ring-[#374049]/30 border-[#374049]"
                                  : ""
                              }`}
                              aria-label={`Go to album photo ${photoIndex + 1}`}
                            >
                              <img
                                src={photo.url}
                                alt=""
                                aria-hidden="true"
                                loading="lazy"
                                decoding="async"
                                className="h-full w-full object-cover"
                              />

                              <span className="absolute bottom-0 right-0 min-w-[1rem] bg-black/65 px-0.5 text-center text-[8px] leading-3 text-white">
                                {photoIndex + 1}
                              </span>

                              {orderEditMode &&
                                pendingSwapPhotoIndex === photoIndex && (
                                  <div
                                    className="absolute inset-0 z-30 flex items-center justify-center bg-white/90"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      type="button"
                                      onClick={confirmCollectionSwap}
                                      className="rounded-md bg-[#24c3be] px-2 py-0.5 text-[9px] text-white shadow-sm"
                                    >
                                      Swap
                                    </button>
                                  </div>
                                )}
                            </button>
                          );
                        },
                      )}
                    </div>
                  </div>
                </div>
                <div className="relative h-[calc(100dvh-5.5rem)] w-screen max-w-none bg-[#374049] p-2 shadow-2xl lg:h-[min(78vh,46rem)] lg:w-[min(96vw,90rem)] lg:border lg:border-white/10 lg:p-3">
                  <div
                    className="relative h-full touch-pan-y overflow-hidden border border-black/20 bg-[#FAFAEB] shadow-inner lg:grid lg:grid-cols-2 lg:gap-0 lg:bg-[#374049] lg:[perspective:1800px]"
                    onTouchStart={(event) => {
                      if (isDesktopAlbum || mobilePageAnimating) return;

                      touchStartXRef.current =
                        event.touches[0]?.clientX ?? null;
                      setMobilePageOffset(0);
                    }}
                    onTouchMove={(event) => {
                      if (
                        isDesktopAlbum ||
                        mobilePageAnimating ||
                        touchStartXRef.current === null
                      ) {
                        return;
                      }

                      const currentX = event.touches[0]?.clientX;
                      if (typeof currentX !== "number") return;

                      let distance = currentX - touchStartXRef.current;
                      const draggingPastStart =
                        mobileAlbumPage === 0 && distance > 0;
                      const draggingPastEnd =
                        mobileAlbumPage >= totalMobileAlbumPages - 1 &&
                        distance < 0;

                      if (draggingPastStart || draggingPastEnd) {
                        distance *= 0.22;
                      }

                      const maximumDrag = window.innerWidth * 0.92;
                      setMobilePageOffset(
                        Math.max(-maximumDrag, Math.min(maximumDrag, distance)),
                      );
                    }}
                    onTouchEnd={(event) => {
                      if (
                        isDesktopAlbum ||
                        mobilePageAnimating ||
                        touchStartXRef.current === null
                      ) {
                        return;
                      }

                      const endX = event.changedTouches[0]?.clientX;
                      const distance =
                        typeof endX === "number"
                          ? endX - touchStartXRef.current
                          : 0;

                      touchStartXRef.current = null;
                      const nextPage =
                        distance < 0
                          ? mobileAlbumPage + 1
                          : mobileAlbumPage - 1;

                      if (
                        Math.abs(distance) < 45 ||
                        nextPage < 0 ||
                        nextPage >= totalMobileAlbumPages
                      ) {
                        settleMobilePage();
                        return;
                      }

                      animateMobilePageTo(nextPage);
                    }}
                    onTouchCancel={() => {
                      touchStartXRef.current = null;
                      settleMobilePage();
                    }}
                  >
                    <div className="relative hidden h-full border-b border-black/35 bg-[#FAFAEB] bg-[repeating-linear-gradient(135deg,rgba(214,203,150,0.12)_0px,rgba(214,203,150,0.12)_1px,transparent_1px,transparent_8px)] p-5 sm:p-7 lg:block lg:border-b-0 lg:border-r">
                      <span className="absolute bottom-3 left-5 z-30 text-[10px] font-medium tracking-[0.16em] text-[#374049]/45">
                        {albumPage === 0 ? "COVER" : albumPage * 2}
                      </span>
                      <div className="pointer-events-none absolute -top-px -bottom-px left-0 z-5 w-4 bg-[#FFFFEB] shadow-[2px_0_3px_rgba(0,0,0,0.12)]">
                        <div className="absolute right-0 top-0 h-full w-px bg-white/80" />
                        <div className="absolute left-0 top-0 h-full w-px bg-[#e8e3c5]" />
                      </div>
                      <div className="absolute right-0 top-0 h-full w-8 border-r border-black/30 bg-[#2d363e] shadow-inner" />
                      <div className="absolute right-3 top-0 h-full w-px bg-white/10" />
                      <div className="absolute right-6 top-0 h-full w-px bg-black/30" />
                      {albumPage !== 0 && (
                        <div className="pointer-events-none absolute inset-y-0 left-[14px] right-10 z-20 overflow-hidden border-y border-white/70 bg-[#374049]/[0.10] shadow-[inset_0_1px_0_rgba(255,255,255,0.85),inset_0_-1px_0_rgba(0,0,0,0.16),inset_0_0_18px_rgba(255,255,255,0.16)]">
                          <div className="absolute -left-[18%] top-0 h-full w-[28%] rotate-[12deg] bg-white/10 blur-lg" />
                          <div className="absolute left-0 top-0 h-full w-px bg-white/45" />
                          <div className="absolute right-0 top-0 h-full w-px bg-white/45" />
                        </div>
                      )}
                      {albumPage === 0 ? (
                        <div className="relative z-20 flex h-full flex-col justify-center overflow-y-auto text-center">
                          {editingAlbumDetails ? (
                            <div className="relative ml-0 mr-8 flex h-full min-w-0 max-w-none flex-col justify-center px-2">
                              <div className="absolute right-0 top-0 z-30 flex gap-2">
                                <button
                                  type="button"
                                  onClick={saveAlbumDetails}
                                  disabled={savingAlbumDetails}
                                  className="rounded-md border border-[#24c3be]/35 bg-white/70 px-3 py-0.5 text-[11px] font-medium text-[#374049] shadow-sm transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {savingAlbumDetails ? "Saving..." : "Save"}
                                </button>

                                <button
                                  type="button"
                                  disabled={
                                    deletingCollection || adminCollectionSaving
                                  }
                                  onClick={requestCollectionDeletion}
                                  className="rounded-md border border-red-300 bg-red-50 px-3 py-1 text-[11px] font-medium text-red-600 shadow-sm transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {deletingCollection
                                    ? "Deleting..."
                                    : "Delete Collection"}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => {
                                    setAlbumTitleDraft(albumTitle);
                                    setAlbumDescriptionDraft(
                                      albumDescription === "No description"
                                        ? ""
                                        : albumDescription,
                                    );
                                    setEditingAlbumDetails(false);
                                  }}
                                  className="rounded-md border border-[#374049]/15 bg-white/55 px-3 py-1 text-[11px] font-medium text-[#374049]/55 shadow-sm transition hover:bg-white hover:text-[#374049]"
                                >
                                  Cancel
                                </button>
                              </div>

                              <div className="mx-auto mb-7 flex w-52 max-w-full items-center justify-center gap-3">
                                <div className="h-px flex-1 bg-[#93ca87]/45" />
                                <div className="h-2.5 w-2.5 shrink-0 rounded-full border border-[#93ca87]/60 bg-[#FCFFF6]" />
                                <div className="h-px flex-1 bg-[#93ca87]/45" />
                              </div>

                              <div className="relative min-w-0 border border-[#374049]/15 bg-[#FCFFF6]/70 px-6 py-8 shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_2px_8px_rgba(55,64,73,0.08)]">
                                <div className="pointer-events-none absolute left-3 top-3 h-8 w-8 border-l border-t border-[#93ca87]/45" />
                                <div className="pointer-events-none absolute right-3 top-3 h-8 w-8 border-r border-t border-[#93ca87]/45" />
                                <div className="pointer-events-none absolute bottom-3 left-3 h-8 w-8 border-b border-l border-[#93ca87]/45" />
                                <div className="pointer-events-none absolute bottom-3 right-3 h-8 w-8 border-b border-r border-[#93ca87]/45" />

                                <input
                                  value={albumTitleDraft}
                                  onChange={(e) =>
                                    setAlbumTitleDraft(e.target.value)
                                  }
                                  maxLength={300}
                                  aria-label="Album title"
                                  className="block w-full min-w-0 border-0 bg-transparent p-0 text-center text-3xl leading-tight text-[#374049] outline-none sm:text-4xl"
                                  style={{ fontFamily: "Cormorant Garamond" }}
                                />

                                <div className="mx-auto mt-5 flex w-36 max-w-full items-center justify-center gap-3">
                                  <div className="h-px flex-1 bg-[#374049]/25" />
                                  <div className="h-2 w-2 shrink-0 rounded-full bg-[#24c3be]" />
                                  <div className="h-px flex-1 bg-[#374049]/25" />
                                </div>

                                <textarea
                                  value={albumDescriptionDraft}
                                  onChange={(e) =>
                                    setAlbumDescriptionDraft(e.target.value)
                                  }
                                  maxLength={300}
                                  placeholder="Add description..."
                                  aria-label="Album description"
                                  rows={4}
                                  className="mx-auto mt-6 block w-full min-w-0 max-w-sm resize-none border-0 bg-transparent p-0 text-center text-[13px] leading-relaxed text-[#374049]/70 outline-none placeholder:text-[#374049]/35"
                                />
                              </div>

                              <div className="mx-auto mt-7 flex w-52 max-w-full items-center justify-center gap-3">
                                <div className="h-px flex-1 bg-[#93ca87]/35" />
                                <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#24c3be]/80" />
                                <div className="h-px flex-1 bg-[#93ca87]/35" />
                              </div>
                            </div>
                          ) : (
                            <div className="relative ml-0 mr-8 flex h-full max-w-none flex-col justify-center px-2 pt-10">
                              {canManageCollections && (
                                <div className="absolute right-0 top-0 z-40">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setAlbumTitleDraft(albumTitle);
                                      setAlbumDescriptionDraft(
                                        albumDescription === "No description"
                                          ? ""
                                          : albumDescription,
                                      );
                                      setEditingAlbumDetails(true);
                                    }}
                                    className="rounded-md bg-[#374049] px-3 py-1 text-[11px] font-medium text-white shadow-sm transition hover:bg-[#01767A]"
                                  >
                                    Admin Edit Collection
                                  </button>
                                </div>
                              )}

                              {isOwnProfile && (
                                <div className="absolute right-0 top-0 z-40 flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setAlbumTitleDraft(albumTitle);
                                      setAlbumDescriptionDraft(
                                        albumDescription === "No description"
                                          ? ""
                                          : albumDescription,
                                      );
                                      setEditingAlbumDetails(true);
                                    }}
                                    className="rounded-md border border-[#374049]/15 bg-white/55 px-3 py-0.5 text-[11px] font-medium text-[#374049]/55 shadow-sm transition hover:bg-white hover:text-[#374049]"
                                  >
                                    Edit
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCollectionViewOpen(false);
                                      setCollectionFullscreen(false);
                                      setCollectionPhotoFullscreen(false);
                                      setCollectionIndex(0);
                                    }}
                                    className="rounded-md border border-[#374049]/15 bg-white/55 px-3 py-0.5 text-[11px] font-medium text-[#374049]/55 shadow-sm transition hover:bg-white hover:text-[#374049]"
                                  >
                                    Close
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedPhotosToAdd([]);
                                      setSelectedSpeciesForPhotoPicker(null);
                                      setAddPhotosOpen(true);
                                    }}
                                    className="rounded-md border border-[#24c3be]/35 bg-white/70 px-3 py-0.5 text-[11px] font-medium text-[#01767a] shadow-sm transition hover:bg-white"
                                  >
                                    Add Photos
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOrderEditMode((prev) => !prev);
                                      cancelCollectionDrag();
                                    }}
                                    className={`rounded-md border px-3 py-0.5 text-[11px] font-medium shadow-sm transition ${
                                      orderEditMode
                                        ? "border-[#24c3be]/35 bg-white text-[#374049]"
                                        : "border-[#374049]/15 bg-white/55 text-[#374049]/55 hover:bg-white hover:text-[#374049]"
                                    }`}
                                  >
                                    {orderEditMode ? "Cancel" : "Reorder"}
                                  </button>

                                  {orderEditMode && (
                                    <button
                                      type="button"
                                      disabled={savingOrder}
                                      onClick={async () => {
                                        if (savingOrder) return;

                                        setSavingOrder(true);

                                        try {
                                          await saveCollectionOrder(
                                            collectionPhotos,
                                          );
                                          setOrderEditMode(false);
                                          cancelCollectionDrag();
                                        } finally {
                                          setSavingOrder(false);
                                        }
                                      }}
                                      className="rounded-md border border-[#24c3be]/35 bg-white/70 px-3 py-0.5 text-[11px] font-medium text-[#374049] shadow-sm transition hover:bg-white disabled:opacity-50"
                                    >
                                      {savingOrder ? "Saving..." : "Save"}
                                    </button>
                                  )}
                                </div>
                              )}

                              <div className="mx-auto mb-7 flex w-52 items-center justify-center gap-3">
                                <div className="h-px flex-1 bg-[#93ca87]/45" />
                                <div className="h-2.5 w-2.5 rounded-full border border-[#93ca87]/60 bg-[#FCFFF6]" />
                                <div className="h-px flex-1 bg-[#93ca87]/45" />
                              </div>

                              <div className="relative border border-[#374049]/15 bg-[#FCFFF6]/70 px-6 py-8 shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_2px_8px_rgba(55,64,73,0.08)]">
                                <div className="pointer-events-none absolute left-3 top-3 h-8 w-8 border-l border-t border-[#93ca87]/45" />
                                <div className="pointer-events-none absolute right-3 top-3 h-8 w-8 border-r border-t border-[#93ca87]/45" />
                                <div className="pointer-events-none absolute bottom-3 left-3 h-8 w-8 border-b border-l border-[#93ca87]/45" />
                                <div className="pointer-events-none absolute bottom-3 right-3 h-8 w-8 border-b border-r border-[#93ca87]/45" />

                                <h2
                                  className="text-3xl sm:text-4xl text-[#374049] leading-tight"
                                  style={{ fontFamily: "Cormorant Garamond" }}
                                >
                                  {albumTitle}
                                </h2>

                                <div className="mx-auto mt-5 flex w-36 items-center justify-center gap-3">
                                  <div className="h-px flex-1 bg-[#374049]/25" />
                                  <div className="h-2 w-2 rounded-full bg-[#24c3be]" />
                                  <div className="h-px flex-1 bg-[#374049]/25" />
                                </div>

                                <p className="mx-auto mt-6 max-w-sm text-[13px] leading-relaxed text-[#374049]/70">
                                  {albumDescription}
                                </p>
                              </div>

                              <div className="mx-auto mt-7 flex w-52 items-center justify-center gap-3">
                                <div className="h-px flex-1 bg-[#93ca87]/35" />
                                <div className="h-1.5 w-1.5 rounded-full bg-[#24c3be]/80" />
                                <div className="h-px flex-1 bg-[#93ca87]/35" />
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="relative z-10 flex h-full flex-col justify-center gap-5 overflow-hidden">
                          {renderAlbumRows(backgroundAlbumSpread.leftRows)}
                        </div>
                      )}
                    </div>
                    <div className="relative hidden h-full bg-[#FAFAEB] bg-[repeating-linear-gradient(135deg,rgba(214,203,150,0.12)_0px,rgba(214,203,150,0.12)_1px,transparent_1px,transparent_8px)] p-5 sm:p-7 lg:block">
                      <span className="absolute bottom-3 right-5 z-30 text-[10px] font-medium tracking-[0.16em] text-[#374049]/45">
                        {albumPage * 2 + 1}
                      </span>
                      <div className="pointer-events-none absolute -top-px -bottom-px right-0 z-5 w-4 bg-[#FFFFEB] shadow-[-2px_0_3px_rgba(0,0,0,0.12)]">
                        <div className="absolute left-0 top-0 h-full w-px bg-white/80" />
                        <div className="absolute right-0 top-0 h-full w-px bg-[#e8e3c5]" />
                      </div>
                      <div className="absolute left-0 top-0 h-full w-8 border-l border-black/30 bg-[#2d363e] shadow-inner" />
                      <div className="absolute left-3 top-0 h-full w-px bg-white/10" />
                      <div className="absolute left-6 top-0 h-full w-px bg-black/30" />

                      <div className="pointer-events-none absolute inset-y-0 left-10 right-[14px] z-20 overflow-hidden border-y border-white/70 bg-[#374049]/[0.10] shadow-[inset_0_1px_0_rgba(255,255,255,0.85),inset_0_-1px_0_rgba(0,0,0,0.16),inset_0_0_18px_rgba(255,255,255,0.16)]">
                        <div className="absolute -right-[18%] top-0 h-full w-[28%] -rotate-[12deg] bg-white/10 blur-lg" />
                        <div className="absolute left-0 top-0 h-full w-px bg-white/45" />
                        <div className="absolute right-0 top-0 h-full w-px bg-white/45" />
                      </div>

                      <div className="relative z-10 flex h-full flex-col justify-center gap-5 overflow-hidden">
                        {renderAlbumRows(backgroundAlbumSpread.rightRows)}
                      </div>
                    </div>
                    <div
                      className="relative h-full bg-[#FAFAEB] bg-[repeating-linear-gradient(135deg,rgba(214,203,150,0.08)_0px,rgba(214,203,150,0.08)_1px,transparent_1px,transparent_9px)] p-3 pb-7 sm:p-7 lg:hidden"
                      style={{
                        transform: `translate3d(${mobilePageOffset}px, 0, 0)`,
                        transition: mobilePageAnimating
                          ? "transform 220ms cubic-bezier(0.22, 0.61, 0.36, 1)"
                          : "none",
                        willChange: "transform",
                      }}
                    >
                      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/80" />
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2 bg-gradient-to-t from-[#dcd8bd]/45 to-transparent" />
                      <span className="absolute bottom-2 right-4 z-30 text-[10px] font-medium tracking-[0.16em] text-[#374049]/45">
                        {mobileAlbumPage === 0 ? "COVER" : mobileAlbumPage}
                      </span>

                      {mobileAlbumPageIsTitle ? (
                        <div className="relative z-20 flex h-full flex-col justify-center overflow-y-auto text-center">
                          <div className="relative flex h-full max-w-none flex-col justify-center px-2 pt-10">
                            {editingAlbumDetails ? (
                              <div className="relative z-40 mx-auto w-full min-w-0 max-w-full space-y-4 overflow-hidden px-2">
                                <input
                                  value={albumTitleDraft}
                                  onChange={(e) =>
                                    setAlbumTitleDraft(e.target.value)
                                  }
                                  maxLength={300}
                                  className="box-border block w-full min-w-0 max-w-full rounded-xl border border-[#8b7355]/30 bg-white/90 px-4 py-3 text-center text-xl text-[#374049] outline-none"
                                  style={{ fontFamily: "Cormorant Garamond" }}
                                />

                                <textarea
                                  value={albumDescriptionDraft}
                                  onChange={(e) =>
                                    setAlbumDescriptionDraft(e.target.value)
                                  }
                                  placeholder="Add description..."
                                  className="box-border block min-h-32 w-full min-w-0 max-w-full resize-none rounded-xl border border-[#8b7355]/30 bg-white/90 px-4 py-3 text-center text-sm text-[#374049] outline-none"
                                />

                                <div className="flex flex-wrap justify-center gap-2">
                                  <button
                                    type="button"
                                    onClick={saveAlbumDetails}
                                    disabled={savingAlbumDetails}
                                    className="rounded-md bg-[#24c3be] px-4 py-2 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    {savingAlbumDetails ? "Saving..." : "Save"}
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      setEditingAlbumDetails(false)
                                    }
                                    className="rounded-md border border-[#374049]/20 bg-white px-4 py-2 text-xs font-medium text-[#374049]"
                                  >
                                    Cancel
                                  </button>

                                  <button
                                    type="button"
                                    disabled={
                                      deletingCollection ||
                                      adminCollectionSaving
                                    }
                                    onClick={requestCollectionDeletion}
                                    className="rounded-md border border-red-300 bg-red-50 px-4 py-2 text-xs font-medium text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    {deletingCollection
                                      ? "Deleting..."
                                      : "Delete Collection"}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {canManageCollections && (
                                  <div className="absolute right-0 top-0 z-40">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setAlbumTitleDraft(albumTitle);
                                        setAlbumDescriptionDraft(
                                          albumDescription === "No description"
                                            ? ""
                                            : albumDescription,
                                        );
                                        setEditingAlbumDetails(true);
                                      }}
                                      className="rounded-md bg-[#374049] px-3 py-1 text-[11px] font-medium text-white shadow-sm transition hover:bg-[#01767A]"
                                    >
                                      Admin Edit Collection
                                    </button>
                                  </div>
                                )}

                                {isOwnProfile && (
                                  <div className="absolute right-0 top-0 z-40 flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setAlbumTitleDraft(albumTitle);
                                        setAlbumDescriptionDraft(
                                          albumDescription === "No description"
                                            ? ""
                                            : albumDescription,
                                        );
                                        setEditingAlbumDetails(true);
                                      }}
                                      className="rounded-md border border-[#374049]/15 bg-white/70 px-3 py-0.5 text-[11px] font-medium text-[#374049]/65 shadow-sm transition hover:bg-white hover:text-[#374049]"
                                    >
                                      Edit
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedPhotosToAdd([]);
                                        setSelectedSpeciesForPhotoPicker(null);
                                        setAddPhotosOpen(true);
                                      }}
                                      className="rounded-md border border-[#24c3be]/35 bg-white/80 px-3 py-0.5 text-[11px] font-medium text-[#01767a] shadow-sm transition hover:bg-white"
                                    >
                                      Add Photos
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOrderEditMode((prev) => !prev);
                                        cancelCollectionDrag();
                                      }}
                                      className={`rounded-md border px-3 py-0.5 text-[11px] font-medium shadow-sm transition ${
                                        orderEditMode
                                          ? "border-[#24c3be]/35 bg-white text-[#374049]"
                                          : "border-[#374049]/15 bg-white/70 text-[#374049]/65 hover:bg-white hover:text-[#374049]"
                                      }`}
                                    >
                                      {orderEditMode ? "Cancel" : "Reorder"}
                                    </button>

                                    {orderEditMode && (
                                      <button
                                        type="button"
                                        disabled={savingOrder}
                                        onClick={async () => {
                                          if (savingOrder) return;

                                          setSavingOrder(true);

                                          try {
                                            await saveCollectionOrder(
                                              collectionPhotos,
                                            );
                                            setOrderEditMode(false);
                                            cancelCollectionDrag();
                                          } finally {
                                            setSavingOrder(false);
                                          }
                                        }}
                                        className="rounded-md border border-[#24c3be]/35 bg-white/80 px-3 py-0.5 text-[11px] font-medium text-[#374049] shadow-sm transition hover:bg-white disabled:opacity-50"
                                      >
                                        {savingOrder ? "Saving..." : "Save"}
                                      </button>
                                    )}
                                  </div>
                                )}
                                <div className="mx-auto mb-7 flex w-52 items-center justify-center gap-3">
                                  <div className="h-px flex-1 bg-[#93ca87]/45" />
                                  <div className="h-2.5 w-2.5 rounded-full border border-[#93ca87]/60 bg-[#FCFFF6]" />
                                  <div className="h-px flex-1 bg-[#93ca87]/45" />
                                </div>

                                <div className="relative border border-[#374049]/15 bg-[#FCFFF6]/70 px-6 py-8 shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_2px_8px_rgba(55,64,73,0.08)]">
                                  <h2
                                    className="text-4xl text-[#374049] leading-tight"
                                    style={{ fontFamily: "Cormorant Garamond" }}
                                  >
                                    {albumTitle}
                                  </h2>

                                  <p className="mx-auto mt-6 max-w-sm text-sm leading-relaxed text-[#374049]/70">
                                    {albumDescription}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="relative z-10 flex h-full flex-col justify-center gap-5 overflow-hidden">
                          {renderAlbumRows(mobileAlbumRows)}
                        </div>
                      )}
                    </div>
                    <div className="pointer-events-none absolute inset-y-0 left-1/2 z-[60] hidden w-px -translate-x-1/2 bg-black/25 shadow-[0_0_2px_rgba(0,0,0,0.25)] lg:block" />{" "}
                    {pageTurn && (
                      <div
                        className={`pointer-events-none absolute top-0 bottom-0 z-40 hidden transition-transform duration-[340ms] ease-[cubic-bezier(.33,.02,.17,1)] lg:block ${
                          pageTurn.direction === "next"
                            ? "left-1/2 right-0 origin-left"
                            : "left-0 right-1/2 origin-right"
                        } ${
                          pageTurn.active
                            ? pageTurn.direction === "next"
                              ? "[-webkit-transform:rotateY(-178deg)] [transform:rotateY(-178deg)]"
                              : "[-webkit-transform:rotateY(178deg)] [transform:rotateY(178deg)]"
                            : "[transform:rotateY(0deg)]"
                        }`}
                        style={{ transformStyle: "preserve-3d" }}
                      >
                        {[0, 180].map((rotation) => (
                          <div
                            key={`turn-face-${rotation}`}
                            className="absolute inset-0 bg-[#FAFAEB] bg-[repeating-linear-gradient(135deg,rgba(214,203,150,0.12)_0px,rgba(214,203,150,0.12)_1px,transparent_1px,transparent_8px)] p-5 sm:p-7 shadow-2xl [backface-visibility:hidden]"
                            style={{ transform: `rotateY(${rotation}deg)` }}
                          >
                            {pageTurn.direction === "next" ? (
                              rotation === 0 ? (
                                <>
                                  <div className="absolute left-0 top-0 h-full w-8 border-l border-black/30 bg-[#2d363e] shadow-inner" />
                                  <div className="absolute left-3 top-0 h-full w-px bg-white/10" />
                                  <div className="absolute left-6 top-0 h-full w-px bg-black/30" />

                                  <div className="pointer-events-none absolute -top-px -bottom-px right-0 z-30 w-4 bg-[#FFFFEB] shadow-[-2px_0_3px_rgba(0,0,0,0.12)]">
                                    <div className="absolute left-0 top-0 h-full w-px bg-white/80" />
                                    <div className="absolute right-0 top-0 h-full w-px bg-[#e8e3c5]" />
                                  </div>

                                  <div className="pointer-events-none absolute inset-y-0 left-10 right-[14px] z-20 overflow-hidden border-y border-white/70 bg-[#374049]/[0.10] shadow-[inset_0_1px_0_rgba(255,255,255,0.85),inset_0_-1px_0_rgba(0,0,0,0.16),inset_0_0_18px_rgba(255,255,255,0.16)]">
                                    <div className="absolute -right-[18%] top-0 h-full w-[28%] -rotate-[12deg] bg-white/10 blur-lg" />
                                    <div className="absolute left-0 top-0 h-full w-px bg-white/45" />
                                    <div className="absolute right-0 top-0 h-full w-px bg-white/45" />
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="pointer-events-none absolute -top-px -bottom-px left-0 z-30 w-4 bg-[#FFFFEB] shadow-[2px_0_3px_rgba(0,0,0,0.12)]">
                                    <div className="absolute right-0 top-0 h-full w-px bg-white/80" />
                                    <div className="absolute left-0 top-0 h-full w-px bg-[#e8e3c5]" />
                                  </div>

                                  <div className="absolute right-0 top-0 h-full w-8 border-r border-black/30 bg-[#2d363e] shadow-inner" />
                                  <div className="absolute right-3 top-0 h-full w-px bg-white/10" />
                                  <div className="absolute right-6 top-0 h-full w-px bg-black/30" />

                                  <div className="pointer-events-none absolute inset-y-0 left-[14px] right-10 z-20 overflow-hidden border-y border-white/70 bg-[#374049]/[0.10] shadow-[inset_0_1px_0_rgba(255,255,255,0.85),inset_0_-1px_0_rgba(0,0,0,0.16),inset_0_0_18px_rgba(255,255,255,0.16)]">
                                    <div className="absolute -left-[18%] top-0 h-full w-[28%] rotate-[12deg] bg-white/10 blur-lg" />
                                    <div className="absolute left-0 top-0 h-full w-px bg-white/45" />
                                    <div className="absolute right-0 top-0 h-full w-px bg-white/45" />
                                  </div>
                                </>
                              )
                            ) : rotation === 0 ? (
                              <>
                                <div className="pointer-events-none absolute -top-px -bottom-px left-0 z-30 w-4 bg-[#FFFFEB] shadow-[2px_0_3px_rgba(0,0,0,0.12)]">
                                  <div className="absolute right-0 top-0 h-full w-px bg-white/80" />
                                  <div className="absolute left-0 top-0 h-full w-px bg-[#e8e3c5]" />
                                </div>

                                <div className="absolute right-0 top-0 h-full w-8 border-r border-black/30 bg-[#2d363e] shadow-inner" />
                                <div className="absolute right-3 top-0 h-full w-px bg-white/10" />
                                <div className="absolute right-6 top-0 h-full w-px bg-black/30" />

                                <div className="pointer-events-none absolute inset-y-0 left-[14px] right-10 z-20 overflow-hidden border-y border-white/70 bg-[#374049]/[0.10] shadow-[inset_0_1px_0_rgba(255,255,255,0.85),inset_0_-1px_0_rgba(0,0,0,0.16),inset_0_0_18px_rgba(255,255,255,0.16)]">
                                  <div className="absolute -left-[18%] top-0 h-full w-[28%] rotate-[12deg] bg-white/10 blur-lg" />
                                  <div className="absolute left-0 top-0 h-full w-px bg-white/45" />
                                  <div className="absolute right-0 top-0 h-full w-px bg-white/45" />
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="absolute left-0 top-0 h-full w-8 border-l border-black/30 bg-[#2d363e] shadow-inner" />
                                <div className="absolute left-3 top-0 h-full w-px bg-white/10" />
                                <div className="absolute left-6 top-0 h-full w-px bg-black/30" />

                                <div className="pointer-events-none absolute -top-px -bottom-px right-0 z-30 w-4 bg-[#FFFFEB] shadow-[-2px_0_3px_rgba(0,0,0,0.12)]">
                                  <div className="absolute left-0 top-0 h-full w-px bg-white/80" />
                                  <div className="absolute right-0 top-0 h-full w-px bg-[#e8e3c5]" />
                                </div>

                                <div className="pointer-events-none absolute inset-y-0 left-10 right-[14px] z-20 overflow-hidden border-y border-white/70 bg-[#374049]/[0.10] shadow-[inset_0_1px_0_rgba(255,255,255,0.85),inset_0_-1px_0_rgba(0,0,0,0.16),inset_0_0_18px_rgba(255,255,255,0.16)]">
                                  <div className="absolute -right-[18%] top-0 h-full w-[28%] -rotate-[12deg] bg-white/10 blur-lg" />
                                  <div className="absolute left-0 top-0 h-full w-px bg-white/45" />
                                  <div className="absolute right-0 top-0 h-full w-px bg-white/45" />
                                </div>
                              </>
                            )}

                            <div className="relative z-10 flex h-full flex-col justify-center gap-5 overflow-hidden">
                              {renderAlbumRows(
                                rotation === 0
                                  ? pageTurn.direction === "next"
                                    ? committedAlbumSpread.rightRows
                                    : committedAlbumSpread.leftRows
                                  : pageTurn.direction === "next"
                                    ? albumSpreads[pageTurn.to]?.leftRows || []
                                    : albumSpreads[pageTurn.to]?.rightRows ||
                                      [],
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {!collectionFullscreen && !collectionPhotoFullscreen && (
                    <>
                      <div className="pointer-events-none absolute inset-y-5 left-0 right-0 z-[85] hidden lg:block">
                        <button
                          type="button"
                          disabled={albumPage === 0 || pageTurn}
                          onClick={() => turnAlbumPage(albumPage - 1)}
                          className={`pointer-events-auto absolute left-7 top-1/2 flex h-11 w-8 -translate-y-1/2 items-center justify-center border border-[#93ca87]/35 bg-[#FCFFF6]/75 text-2xl leading-none text-[#374049] shadow-[0_0_12px_rgba(147,202,135,0.35),inset_0_1px_0_rgba(255,255,255,0.75)] backdrop-blur-sm transition ${
                            albumPage === 0 || pageTurn
                              ? "opacity-0 pointer-events-none"
                              : "opacity-80 hover:opacity-100 hover:-translate-x-0.5 hover:shadow-[0_0_18px_rgba(36,195,190,0.35),inset_0_1px_0_rgba(255,255,255,0.85)]"
                          }`}
                          aria-label="Previous page"
                        >
                          ‹
                        </button>

                        <button
                          type="button"
                          disabled={
                            albumPage >= totalAlbumPages - 1 || pageTurn
                          }
                          onClick={() => turnAlbumPage(albumPage + 1)}
                          className={`pointer-events-auto absolute right-7 top-1/2 flex h-11 w-8 -translate-y-1/2 items-center justify-center border border-[#93ca87]/35 bg-[#FCFFF6]/75 text-2xl leading-none text-[#374049] shadow-[0_0_12px_rgba(147,202,135,0.35),inset_0_1px_0_rgba(255,255,255,0.75)] backdrop-blur-sm transition ${
                            albumPage >= totalAlbumPages - 1 || pageTurn
                              ? "opacity-0 pointer-events-none"
                              : "opacity-80 hover:opacity-100 hover:translate-x-0.5 hover:shadow-[0_0_18px_rgba(36,195,190,0.35),inset_0_1px_0_rgba(255,255,255,0.85)]"
                          }`}
                          aria-label="Next page"
                        >
                          ›
                        </button>
                      </div>
                      <div className="pointer-events-none absolute inset-y-5 left-0 right-0 z-[85] lg:hidden">
                        <button
                          type="button"
                          disabled={
                            mobileAlbumPage === 0 || mobilePageAnimating
                          }
                          onClick={() => {
                            setPageTurn(null);
                            animateMobilePageTo(mobileAlbumPage - 1);
                          }}
                          className={`pointer-events-auto absolute left-2 top-1/2 flex h-12 w-9 -translate-y-1/2 items-center justify-center rounded-r-full border border-l-0 border-[#93ca87]/35 bg-[#FCFFF6]/90 text-2xl leading-none text-[#374049] shadow-md transition ${
                            mobileAlbumPage === 0
                              ? "opacity-0 pointer-events-none"
                              : "opacity-90 active:scale-95"
                          }`}
                          aria-label="Previous page"
                        >
                          ‹
                        </button>

                        <button
                          type="button"
                          disabled={
                            mobileAlbumPage >= totalMobileAlbumPages - 1 ||
                            mobilePageAnimating
                          }
                          onClick={() => {
                            setPageTurn(null);
                            animateMobilePageTo(mobileAlbumPage + 1);
                          }}
                          className={`pointer-events-auto absolute right-2 top-1/2 flex h-12 w-9 -translate-y-1/2 items-center justify-center rounded-l-full border border-r-0 border-[#93ca87]/35 bg-[#FCFFF6]/90 text-2xl leading-none text-[#374049] shadow-md transition ${
                            mobileAlbumPage >= totalMobileAlbumPages - 1
                              ? "opacity-0 pointer-events-none"
                              : "opacity-90 active:scale-95"
                          }`}
                          aria-label="Next page"
                        >
                          ›
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {editingForm && (
              <div
                className="fixed inset-0 z-[80] flex items-end justify-center bg-black/55 sm:items-center sm:px-4 sm:py-6"
                onClick={(event) => {
                  event.stopPropagation();
                  closePhotoEditor();
                }}
              >
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="collection-photo-editor-title"
                  className="flex max-h-[94dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.75rem] border border-white/60 bg-[#f6f8f7] shadow-2xl sm:rounded-[1.75rem]"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="flex items-center gap-4 border-b border-[#93ca87]/30 bg-white px-4 py-3 sm:px-6">
                    <img
                      src={editingForm.url}
                      alt=""
                      aria-hidden="true"
                      className="h-14 w-14 shrink-0 rounded-xl border border-[#93ca87]/30 object-cover"
                    />

                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#01767A]">
                        Photo {collectionIndex + 1} of {collectionPhotos.length}
                      </div>
                      <h2
                        id="collection-photo-editor-title"
                        className="truncate text-2xl italic text-[#374049]"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        {currentCollectionSpecies?.name ||
                          currentCollectionPhoto?.species ||
                          "Edit observation"}
                      </h2>
                    </div>

                    <button
                      type="button"
                      onClick={closePhotoEditor}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-lg text-slate-500 transition hover:bg-slate-50"
                      aria-label="Close photo editor"
                    >
                      ×
                    </button>
                  </div>

                  <div className="overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <LifeformPicker
                        scientificName={currentCollectionSpecies?.name || currentCollectionPhoto?.species || ""}
                        value={editingForm.label || ""}
                        selectedKingdom={editingForm.lifeformKingdom || ""}
                        onChange={(value) => setEditingForm((previous) => ({
                          ...previous, label: value,
                        }))}
                        onKingdomChange={(value) => setEditingForm((previous) => ({
                          ...previous, lifeformKingdom: value, label: "",
                        }))}
                      />

                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-slate-600">
                          Breed / Subspecies / Variety
                        </span>
                        <input
                          value={editingForm.variant || ""}
                          onChange={(event) =>
                            setEditingForm((previous) => ({
                              ...previous,
                              variant: event.target.value,
                            }))
                          }
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-[#374049] outline-none focus:border-[#24c3be] focus:ring-2 focus:ring-[#24c3be]/15"
                          placeholder="NA"
                        />
                      </label>
                    </div>

                    <fieldset className="mt-4">
                      <legend className="mb-1 text-xs font-semibold text-slate-600">
                        Date
                      </legend>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          ["year", "Year", 4],
                          ["month", "Month", 2],
                          ["day", "Day", 2],
                        ].map(([key, placeholder, maxLength]) => (
                          <input
                            key={key}
                            inputMode="numeric"
                            maxLength={maxLength}
                            value={
                              editingForm?.date?.[key] === "NA"
                                ? ""
                                : editingForm?.date?.[key] || ""
                            }
                            onChange={(event) =>
                              setEditingForm((previous) => ({
                                ...previous,
                                date: {
                                  ...previous.date,
                                  [key]: event.target.value || "NA",
                                },
                              }))
                            }
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-[#374049] outline-none focus:border-[#24c3be] focus:ring-2 focus:ring-[#24c3be]/15"
                            placeholder={placeholder}
                            aria-label={placeholder}
                          />
                        ))}
                      </div>
                    </fieldset>

                    <label className="mt-4 block">
                      <span className="mb-1 block text-xs font-semibold text-slate-600">
                        Location
                      </span>
                      <input
                        value={
                          editingForm.locationName || editingForm.location || ""
                        }
                        onChange={(event) => {
                          const locationName = event.target.value;

                          setEditingForm((previous) => ({
                            ...previous,
                            location: locationName,
                            locationName,
                            locationLat: null,
                            locationLng: null,
                            locationCountry: "",
                            locationAdminArea: "",
                            locationCity: "",
                            locationPlaceId: "",
                          }));
                        }}
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-[#374049] outline-none focus:border-[#24c3be] focus:ring-2 focus:ring-[#24c3be]/15"
                        placeholder="No location recorded"
                      />
                      <span className="mt-1 block text-[11px] leading-relaxed text-slate-400">
                        Editing this text clears old map coordinates so the name
                        and pin cannot disagree.
                      </span>
                    </label>

                    <label className="mt-4 block">
                      <span className="mb-1 flex items-center justify-between text-xs font-semibold text-slate-600">
                        <span>Notes</span>
                        <span className="font-normal text-slate-400">
                          {String(editingForm.notes || "").length}/2000
                        </span>
                      </span>
                      <textarea
                        value={editingForm.notes || ""}
                        onChange={(event) =>
                          setEditingForm((previous) => ({
                            ...previous,
                            notes: event.target.value,
                          }))
                        }
                        maxLength={2000}
                        rows={4}
                        className="w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm leading-relaxed text-[#374049] outline-none focus:border-[#24c3be] focus:ring-2 focus:ring-[#24c3be]/15"
                        placeholder="Add field observations, behavior, habitat details, or identification notes..."
                      />
                    </label>

                    <div className="mt-4">
                      <label className="mb-1 block text-xs font-semibold text-slate-600">
                        Collections
                      </label>
                      <select
                        defaultValue=""
                        onChange={(event) => {
                          const selectedCollection = event.target.value;
                          if (!selectedCollection) return;

                          if (selectedCollection === "__new__") {
                            const name = window.prompt("New collection name:");
                            if (name?.trim()) {
                              setEditingForm((previous) => ({
                                ...previous,
                                collections: [
                                  ...new Set([
                                    ...(previous.collections || []),
                                    name.trim(),
                                  ]),
                                ],
                              }));
                            }
                          } else {
                            setEditingForm((previous) => {
                              const currentCollections =
                                previous.collections || [];
                              const exists =
                                currentCollections.includes(selectedCollection);

                              return {
                                ...previous,
                                collections: exists
                                  ? currentCollections.filter(
                                      (name) => name !== selectedCollection,
                                    )
                                  : [...currentCollections, selectedCollection],
                              };
                            });
                          }

                          event.target.value = "";
                        }}
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-[#374049] outline-none focus:border-[#24c3be] focus:ring-2 focus:ring-[#24c3be]/15"
                      >
                        <option value="">
                          Add to or remove from a collection
                        </option>
                        <option value="__new__">+ Create new collection</option>
                        {Object.keys(collections || {})
                          .sort()
                          .map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                      </select>

                      {editingForm.collections?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {editingForm.collections.map((name) => (
                            <button
                              key={name}
                              type="button"
                              onClick={() =>
                                setEditingForm((previous) => ({
                                  ...previous,
                                  collections: previous.collections.filter(
                                    (collection) => collection !== name,
                                  ),
                                }))
                              }
                              className="rounded-full border border-[#93ca87]/35 bg-white px-2.5 py-1 text-xs text-[#374049] transition hover:border-red-200 hover:text-red-600"
                              aria-label={`Remove from ${name}`}
                            >
                              {name} ×
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 border-t border-[#93ca87]/30 bg-white px-4 py-3 sm:px-6">
                    <button
                      type="button"
                      onClick={closePhotoEditor}
                      disabled={savingPhotoMetadata}
                      className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-[#374049] transition hover:bg-slate-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSavePhotoMetadata}
                      disabled={savingPhotoMetadata}
                      className="rounded-xl bg-[#01767A] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#005f63] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {savingPhotoMetadata ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
